/**
 * AI Generation Mixin
 * Handles AI image generation functionality
 */

import { markRaw } from 'vue';

const ai_generation_mixin = {
    data() {
        return {
            // AI Generation state
            ai_generation_active: false,
            ai_generating: false,
            ai_queue_position: 0,
            ai_queue_status: 'idle',
            ai_preview_image: './img/ai/sample.webp',
            is_generated_image: false, // Distinguish between sample and generated images
            ai_panel_ready: false, // Panel DOM control flag
            
            // Panel expansion state
            ai_panel_expanded: false, // Panel maximized/restored state
            ai_panel_width: 762, // Default width (41rem = 656px)
            ai_panel_min_width: 762, // Minimum width (same as default)
            ai_panel_resizing: false, // Resize state flag
            
            // Unified timestamp-based tracking
            queue_timer: null,
            generation_start_time: null,
            button_press_time: null,
            my_request_timestamp: null, // Timestamp assigned by server for queue tracking
            processing_message_shown: false, // Flag to prevent duplicate "processing started" messages
            
            // Size presets orientation - START VERTICAL (true means rotated to vertical)
            presets_rotated: true,

            // Mikogen UI State
            mikogen_collapsed_sections: {
                positive_prompt: false,
                negative_prompt: false,
                size_presets: false,
                advanced_settings: false,
                hires_fix: false,
                generation_history: true
            },
            
            // AI Generation settings
            ai_settings: {
                model: 'mikomic_anime_base_v3.safetensors',
                style: 2, // 1 = no style, 2 = anime, 3 = realistic, 4 = realistic++
                width: 1024,
                height: 1536,
                steps: 23, 
                cfg_scale: 6.5,
                sampler: 'dpmpp_2m',
                scheduler: 'beta',
                hires_denoise: 0.47,
                use_hires_fix: true,
                use_prompt_hand_fixer: false,
                use_same_seed_for_hires: false,
                seed: -1,
                use_enhanced_prompt: true,
                use_mikomic_negative: 2, // 1=custom, 2=general, 3=detailed, 4=custom+general, 5=custom+detailed
                reinforce_anime: 0, // Integer value 0-7
                reinforce_background: false
            },
            
            // AI prompts
            ai_prompts: {
                positive: '',
                negative: ''
            },

            sample_prompts:{
                sample_pos_pro: '((young girl)), pink dinosaur outfit, sitting, cinematic lighting, light pink hair, (sidelight), night, window, focus to eyes, happy, close up, hood ',
                //sample_pos_pro: 'happy, feminine, dark setting, sitting, cute grin, soft anatomy, (shinny skin:1.15), magical, (1girl),  dutch angle, (cinematic:1.2), (sidelight:1.2), (high contrast), (very detailed), detailed clothes, (young girl:1.35), (moe), deep eyes, atmospheric, black ruffles dress, indoors, luxury room,  (perspective),  (light particles), (moe:1.15),  feminine hair, multicolored light hair, cute young girl, (medium breasts), (feminine), charming,',
                sample_pos_flat: 'soft anatomy, (shinny skin:1.15),  light particles, masterpiece,  no lineart,   anime style, 1girl, solo, cute, (moe:1.2), (loli:1.2), (medium breasts), short and petite girl, funny expressions, (perspective), daytime, open mouth, (cute grin), selfie, V, outdoors, beautiful park,  white short dress with blue ribbon,ruffled dress, long white hair with blue ribbon,  loose hair, (long hair), blue eyes, (simple white sneakers), (white thigh-high stockings), charming, feminine, thighs, big eyes, benches and posts'
            },
            
            // Available models
            available_models: [                
                {
                    id: 1,
                    name: 'mikomic_anime_base_v3.safetensors',
                    display_name: 'Mikomic Anime V3',
                    enabled: true,
                    version: 'v3.0',
                    type: 'Checkpoint',
                    description_key: 'ai.model_descriptions.mikomic_anime_v3',
                    demo_images: [
                        './img/ai/demo/mikomic_anime_v3_1.webp',
                        './img/ai/demo/mikomic_anime_v3_2.webp',
                        './img/ai/demo/mikomic_anime_v3_3.webp',
                        './img/ai/demo/mikomic_anime_v3_4.webp',
                        './img/ai/demo/mikomic_anime_v3_5.webp',
                        './img/ai/demo/mikomic_anime_v3_6.webp',
                        './img/ai/demo/mikomic_anime_v3_7.webp',
                        './img/ai/demo/mikomic_anime_v3_8.webp',
                        './img/ai/demo/mikomic_anime_v3_9.webp',
                        './img/ai/demo/mikomic_anime_v3_10.webp',
                        './img/ai/demo/mikomic_anime_v3_11.webp',
                        './img/ai/demo/mikomic_anime_v3_12.webp',
                        './img/ai/demo/mikomic_anime_v3_13.webp',
                        './img/ai/demo/mikomic_anime_v3_14.webp'
                    ]
                },
                {
                    id: 2,
                    name: 'mikoniji_semi_v10.safetensors',
                    display_name: 'Mikoniji V1.2',
                    enabled: true,
                    version: 'v1.2',
                    type: 'Checkpoint',
                    description_key: 'ai.model_descriptions.mikoniji_v12',
                    demo_images: [
                        './img/ai/demo/niji/01.webp',
                        './img/ai/demo/niji/02.webp',
                        './img/ai/demo/niji/03.webp',
                        './img/ai/demo/niji/04.webp',
                        './img/ai/demo/niji/05.webp',
                        './img/ai/demo/niji/06.webp',
                        './img/ai/demo/niji/07.webp',
                        './img/ai/demo/niji/08.webp',
                        './img/ai/demo/niji/09.webp',
                        './img/ai/demo/niji/10.webp',
                        './img/ai/demo/niji/11.webp',
                        './img/ai/demo/niji/12.webp',
                        './img/ai/demo/niji/13.webp',
                        './img/ai/demo/niji/14.webp',
                        './img/ai/demo/niji/15.webp',
                        './img/ai/demo/niji/16.webp',
                        './img/ai/demo/niji/17.webp',
                        './img/ai/demo/niji/18.webp',
                        './img/ai/demo/niji/19.webp',
                        './img/ai/demo/niji/20.webp',
                        './img/ai/demo/niji/21.webp',
                        './img/ai/demo/niji/22.webp',
                        './img/ai/demo/niji/23.webp',
                        './img/ai/demo/niji/24.webp'
                    ]
                }
                
            ],
            
            model_variants: [
                {
                    id: 1,
                    display_name: 'Base model',
                    enabled: true
                },
                {
                    id: 2,
                    display_name: 'Anime Style',
                    enabled: true
                },
                {
                    id: 3,
                    display_name: 'Realistic',
                    enabled: true
                }
                // ,
                // {
                //     id: 4,
                //     display_name: 'Mikomic Realistic++',
                //     enabled: true
                // }
                
            ],
            
            current_variant: 2,
            show_legacy_models: true,
            available_styles: [
                {
                    id: 1,
                    name: 'base'
                },
                {
                    id: 2,
                    name: 'anime'
                },
                {
                    id: 3,
                    name: 'realistic'
                }
            ],
            
            available_niji_styles: [
                {
                    id: 1,
                    name: 'base'
                },
                {
                    id: 2,
                    name: 'stylized'
                }
            ],
            
            // Available samplers
            available_samplers: [
                { name: 'dpmpp_2m', display_name: 'DPM++ 2M' },
                { name: 'res_multistep', display_name: 'Res MultiStep' },
                { name: 'gradient_estimation', display_name: 'Gradient Estimation' },
                { name: 'euler', display_name: 'Euler' },
                { name: 'euler_ancestral', display_name: 'Euler Ancestral' },
                { name: 'dpmpp_2m_sde', display_name: 'DPM++ 2M SDE' }
            ],
            
            // Available schedulers
            available_schedulers: [
                { name: 'beta', display_name: 'Beta' },
                { name: 'karras', display_name: 'Karras' },
                { name: 'simple', display_name: 'Simple' },
                { name: 'normal', display_name: 'Normal' },
                { name: 'sgm_uniform', display_name: 'SGM Uniform' },                
                { name: 'ddim_uniform', display_name: 'DDIM Uniform' },               
                { name: 'kl_optimal', display_name: 'KL Optimal' }
            ],
            
            // Advanced settings collapsed state
            advanced_settings_collapsed: true,
            
            // Image drop zone state
            drop_zone_active: false,

            // Generation history
            show_generation_history: false, // Toggle for history dropdown
            generation_history: [], // Array of historical images
            history_loading: false, // Loading state for history
            history_loaded_from_server: false, // Track if history has been loaded from R2
            history_page: 0, // For lazy loading pagination
            history_per_page: 6, // Images to load per batch
            history_has_more: true, // Whether there are more images to load
            history_total_images: 0, // Total images in server (not just loaded)
            
            // Generation cost - MUST be loaded from server, no defaults
            image_credit_cost: null,
            
            // Previous generation parameters for duplicate detection
            last_generation_params: null,
            
            // Scheduler preferences per model (dynamic, user preferences stored here)
            scheduler_preferences: {},
            
            // Model info modal state
            show_model_info_modal: false,
            current_model_info: null,
            current_demo_image_index: 0,
            
            // Model Info Lightbox State
            model_info_lightbox_active: false,
            model_info_lightbox_index: 0,
        };
    },

    watch: {
        // Force UI update when queue status changes
        ai_queue_status(new_status) {
            console.log('Queue status changed to:', new_status);
            this.$forceUpdate();
        },
        ai_queue_position(new_position) {
            console.log('Queue position changed to:', new_position);
            this.$forceUpdate();
        },

    },

    mounted() {
        // Save initial scheduler preference for default model
        if (this.ai_settings.model && this.ai_settings.scheduler && !this.ai_settings.model.includes('mikoniji_semi')) {
            this.scheduler_preferences[this.ai_settings.model] = this.ai_settings.scheduler;
        }
        
        // Load generation configuration from server
        this.load_generation_config();
        
        // Check for existing generation state on page load
        this.check_existing_generation_state();

        this.refresh_lucide();

        // Responsive interface cleanup - Force collapse states based on device
        if (window.innerWidth < 768) {
            // Mobile: Collapse EVERYTHING
            this.mikogen_collapsed_sections.positive_prompt = true;
            this.mikogen_collapsed_sections.negative_prompt = true;
            this.mikogen_collapsed_sections.size_presets = true;
            this.mikogen_collapsed_sections.advanced_settings = true;
            this.mikogen_collapsed_sections.hires_fix = true;
            this.mikogen_collapsed_sections.generation_history = true;
        } else {
             // Desktop: Collapse NOTHING
             this.mikogen_collapsed_sections.positive_prompt = false; 
             this.mikogen_collapsed_sections.hires_fix = false; 
             this.mikogen_collapsed_sections.generation_history = true; 
             this.mikogen_collapsed_sections.negative_prompt = false;
             this.mikogen_collapsed_sections.size_presets = false;
             this.mikogen_collapsed_sections.advanced_settings = false;
        }
    },

    computed: {
        /**
         * Determine if panel should show 3 columns based on width
         * Breakpoint: 1100px for 3-column layout
         */
        use_three_column_layout() {
            return this.ai_panel_width >= 1100;
        },
        
        /**
         * Auto-expand collapsibles when using 3-column layout
         */
        should_auto_expand_collapsibles() {
            return this.use_three_column_layout;
        },
        
        /**
         * Get available schedulers based on selected model
         */
        filtered_schedulers() {
            const current_model = this.ai_settings.model;
            
            // All Mikoniji models ONLY support Karras
            if (current_model && current_model.includes('mikoniji_semi')) {
                return this.available_schedulers.map(scheduler => ({
                    ...scheduler,
                    disabled: scheduler.name !== 'karras',
                    recommended: scheduler.name === 'karras'
                }));
            }
            
            // For ALL other models (current and future): All schedulers available, Beta recommended
            return this.available_schedulers.map(scheduler => ({
                ...scheduler,
                disabled: false,
                recommended: scheduler.name === 'beta'
            }));
        },
        
        /**
         * Check if current model requires Karras only
         */
        is_karras_only_model() {
            return this.ai_settings.model && this.ai_settings.model.includes('mikoniji_semi');
        },
        
        /**
         * Filter styles based on selected model
         */
        filtered_styles() {
            if (this.ai_settings.model && this.ai_settings.model.includes('mikoniji_semi')) {
                return this.available_niji_styles;
            }
            return this.available_styles;
        },
        
        /**
         * Check if scheduler dropdown should be disabled
         */
        is_scheduler_dropdown_disabled() {
            return this.is_karras_only_model;
        }
    },

    watch: {
        /**
         * Auto-expand collapsibles when switching to 3-column layout
         */
        use_three_column_layout(is_three_column) {
            if (is_three_column) {
                // Auto-expand when switching to 3-column layout
                this.advanced_settings_collapsed = false;
                this.show_generation_history = true;
                
                // Trigger lazy loading if history not loaded yet
                if (!this.history_loaded_from_server && this.generation_history.length === 0) {
                    this.$nextTick(() => {
                        this.load_generation_history();
                    });
                }
            }
        }
    },

    methods: {
        /**
         * Activate AI generation mode
         */
        activate_ai_generation_mode() {
            this.ai_generation_active = true;
            this.change_tool('select_panel'); // Auto-select panel tool as specified in plan
            this.close_all_right_panels(); // Close other panels first
                        //console.log('AI generation mode activated');
        },

        /**
         * Deactivate AI generation mode
         */
        deactivate_ai_generation_mode() {
            this.ai_generation_active = false;
            // DO NOT clear ai_preview_image - preserve generated image
            // DO NOT stop polling or reset ai_generating - let generation continue
            //console.log('AI generation mode deactivated - preserving state');
            this.change_tool('select');
        },

        /**
         * Toggle AI panel maximize/restore state
         */
        toggle_ai_panel_maximize() {
            this.ai_panel_expanded = !this.ai_panel_expanded;
            
            if (this.ai_panel_expanded) {
                // Maximize - use full viewport width minus some margin
                this.ai_panel_width = Math.min(window.innerWidth - 100, 1277);
            } else {
                // Restore to default width
                this.ai_panel_width = this.ai_panel_min_width;
            }
        },

        /**
         * Start panel resize operation
         */
        start_ai_panel_resize(event) {
            this.ai_panel_resizing = true;
            const startX = event.clientX;
            const startWidth = this.ai_panel_width;
            let rafId = null;
            
            const handle_mouse_move = (e) => {
                if (!this.ai_panel_resizing) return;
                
                if (rafId) return;
                
                rafId = requestAnimationFrame(() => {
                    const deltaX = startX - e.clientX; // Moving left increases width
                    const newWidth = Math.max(this.ai_panel_min_width, startWidth + deltaX);
                    
                    // Limit to viewport width
                    this.ai_panel_width = Math.min(newWidth, window.innerWidth - 50);
                    rafId = null;
                });
            };
            
            const handle_mouse_up = () => {
                this.ai_panel_resizing = false;
                if (rafId) {
                    cancelAnimationFrame(rafId);
                    rafId = null;
                }
                document.removeEventListener('mousemove', handle_mouse_move);
                document.removeEventListener('mouseup', handle_mouse_up);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };
            
            document.addEventListener('mousemove', handle_mouse_move);
            document.addEventListener('mouseup', handle_mouse_up);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },

        /**
         * Generate AI image with unified timestamp-based logic
         */
        async generate_ai_image() {
            if (this.ai_generating) return;
            
            // CRITICAL: Check if user already has generation in progress/queue before allowing new generation
            try {
                const response = await this.call_ai_generation_api({
                    action: 'check_queue_status'
                });
                
                if (response.success) {
                    const user_is_processing = response.user_is_processing;
                    const user_queue_timestamp = response.user_queue_timestamp;
                    const user_queue_position = response.user_queue_position;
                    
                    // Block generation if user already has active generation or is in queue
                    if (user_is_processing || (user_queue_timestamp && user_queue_position > 0)) {
                        // Update UI to reflect existing state (same as mounted does)
                        this.ai_generating = true;
                        this.ai_queue_status = user_is_processing ? 'processing' : 'queued';
                        this.ai_queue_position = user_queue_position || 0;
                        this.my_request_timestamp = user_is_processing ? response.user_processing_timestamp : user_queue_timestamp;
                        this.generation_start_time = Date.now();
                        this.start_timestamp_monitoring();
                        
                        return; // Block new generation
                    }
                }
            } catch (error) {
                this.show_floating_message(this.get_t('ai.invalid_request'), 'warning');
                console.error('Error checking queue status before generation:', error);
                return; // Block generation on error to be safe
            }
            
            // Check if a panel is selected for insertion
            if (this.comic_mode && !this.selected_panel) {
                this.show_floating_message(this.get_t('ai.select_panel_first'), 'warning');
                return;
            }

            // Validate selected model is enabled
            if (!this.show_legacy_models) {
                const selected_variant = this.model_variants.find(variant => variant.id === this.current_variant);
                if (!selected_variant || !selected_variant.enabled) {
                    this.show_floating_message(this.get_t('ai.model_not_available'), 'warning');
                    return;
                }
                this.ai_settings.style = this.current_variant;
            } else {
                const selected_model = this.available_models.find(model => model.name === this.ai_settings.model);
                if (!selected_model || !selected_model.enabled) {
                    this.show_floating_message(this.get_t('ai.model_not_available'), 'warning');
                    return;
                }
            }
            
            // Prepare generation parameters
            const params = this.prepare_generation_params();
            // Check for duplicate generation parameters
            if (this.last_generation_params && this.are_params_identical(params, this.last_generation_params)) {
                this.show_floating_message(this.get_t('ai.identical_generation_warning'), 'warning');
                console.log('Duplicate generation detected - stopping generation');
                return;
            }
            
            // IMMEDIATE: Set generating state for button feedback
            this.ai_generating = true;
            this.is_generated_image = false;
            this.generation_start_time = Date.now();
            this.button_press_time = Date.now();
            this.show_floating_message(this.get_t('ai.generation_started'), 'info');
            
            try {
                // Send generation request
                const response = await this.call_ai_generation_api({
                    action: 'generate_image',
                    params: params
                });
                
                if (response.success) {
                    // Store parameters for duplicate detection and get timestamp
                    this.last_generation_params = JSON.parse(JSON.stringify(params));
                    this.my_request_timestamp = response.request_timestamp;
                    
                    // Start unified timestamp monitoring
                    this.start_timestamp_monitoring();
                    
                } else {
                    // Server rejected generation                    
                    if (response.available_credits !== undefined && response.required_credits !== undefined) {
                        this.show_floating_message(
                            `${this.get_t('ai.insufficient_credits')}`, 
                            'error'
                        );
                    } else {
                        this.show_floating_message(response.error || this.get_t('ai.generation_error'), 'error');
                    }
                    
                    this.reset_generation_state();
                }
                
            } catch (error) {
                console.error('AI generation request error:', error);
                this.show_floating_message(this.get_t('ai.generation_error'), 'error');
                this.reset_generation_state();
            }
        },

        /**
         * Start unified timestamp monitoring for both queue and generation
         */
        start_timestamp_monitoring() {
            this.queue_timer = setInterval(() => {
                this.check_timestamp_status();
            }, 3000);
            
            // First check immediately
            this.check_timestamp_status();
        },

        /**
         * Check timestamp status - unified for queue and completion detection
         */
        async check_timestamp_status() {
            if (!this.ai_generating || !this.my_request_timestamp) {
                return;
            }            
            
            try {
                const response = await this.call_ai_generation_api({
                    action: 'check_queue_status'
                });
                
                if (response.success) {
                    // Use processed data from backend - no calculations needed
                    const user_is_processing = response.user_is_processing;
                    const user_processing_timestamp = response.user_processing_timestamp;
                    const user_queue_timestamp = response.user_queue_timestamp;
                    const user_queue_position = response.user_queue_position;
                    
                    // Check if our specific timestamp is being processed
                    if (user_is_processing && user_processing_timestamp === this.my_request_timestamp) {
                        // Our request is currently being processed
                        this.ai_queue_status = 'processing';
                        this.ai_queue_position = 0;
                        
                        // Only show message once when processing starts
                        if (!this.processing_message_shown) {
                            //this.show_floating_message(this.get_t('ai.processing_started'), 'info');
                            this.processing_message_shown = true;
                        }
                        
                        // Reset generation start time to NOW when processing actually begins
                        this.generation_start_time = Date.now();
                        
                        // Wait 7 seconds then check for completion
                        setTimeout(() => {
                            this.check_for_completion();
                        }, 7000);
                    } 
                    
                    else if (user_queue_timestamp === this.my_request_timestamp && user_queue_position > 0) {
                        // Our request is in queue - update position if changed
                        this.ai_queue_status = 'queued';
                        
                        if (this.ai_queue_position !== user_queue_position) {
                            this.ai_queue_position = user_queue_position;
                            this.show_floating_message(`${this.get_t('ai.queued_position')} ${user_queue_position}`, 'info');
                        }
                        
                        // Don't timeout while queued - only when processing
                        return;
                    } 
                    
                    else {
                        // Not in queue and not processing - should be completed
                        //console.log('Timestamp not in queue, checking for completed images...');
                        const completed = await this.check_for_completion();
                        
                        if (!completed) {
                            //console.log('No image found yet, will continue monitoring...');
                        }
                    }
                }
                
                // Global timeout of 45 seconds for all generation states (prevents infinite generating)
                if (Date.now() - this.generation_start_time > 45000) {
                    console.log('Generation timeout reached (45s global timeout)');
                    this.show_floating_message(this.get_t('ai.generation_timeout'), 'error');
                    this.reset_generation_state();
                    return;
                }
                
            } catch (error) {
                console.error('Timestamp status check error:', error);
            }
        },

        /**
         * Check for completion - poll is_generating instead of R2
         */
        async check_for_completion() {
            if (!this.ai_generating) return;
            
            try {
                // First check is_generating status (cheap database query)
                const status_response = await this.call_ai_generation_api({
                    action: 'check_generation_status'
                });
                
                if (status_response.success && !status_response.is_generating) {
                    const response = await this.call_ai_generation_api({
                        action: 'check_user_images',
                        since_timestamp: this.my_request_timestamp
                    });

                    if (!this.ai_generating) return;
                    
                    if (response.success && response.has_new_images && response.images.length > 0) {
                        this.reset_generation_state();
                        this.is_generated_image = true;
                        this.ai_preview_image = response.images[0].url;
                        
                        
                        // Update credits from server
                        if (response.credits_remaining !== undefined) {
                            this.ai_credits = response.credits_remaining;
                        }
                        
                        this.show_floating_message(this.get_t('ai.generation_complete'), 'success');
                        
                        // Add generated image to history array only if not already present
                        const image_already_exists = this.generation_history.some(img => img.url === response.images[0].url);
                        if (!image_already_exists) {
                            const user_tier = this.user_data?.tier || 'free';
                            const max_history = this.tier_limits?.[user_tier]?.image_gen_history_count;
                            
                            this.generation_history.unshift(response.images[0]);
                            
                            // If exceeded limit, remove oldest and don't increment counter
                            if (max_history && this.generation_history.length > max_history) {
                                this.generation_history.splice(max_history);
                            } else {
                                this.history_total_images = (this.history_total_images || 0) + 1;
                            }
                        }                       
                                                
                        return true;
                    } else {
                        console.log('is_generating=false but no image found yet, retrying...');
                        return false;
                    }
                } else {
                    // Still generating, continue polling
                    console.log('Still generating...');
                    return false;
                }
                
            } catch (error) {
                console.error('Completion check error:', error);
                return false;
            }
        },

        /**
         * Reset all generation state
         */
        reset_generation_state() {
            this.ai_generating = false;
            this.ai_queue_status = 'idle';
            this.ai_queue_position = 0;
            this.my_request_timestamp = null;
            this.processing_message_shown = false; // Reset message flag
            
            if (this.queue_timer) {
                clearInterval(this.queue_timer);
                this.queue_timer = null;
            }
        },

        /**
         * Prepare generation parameters
         */
        prepare_generation_params() {
            // Use placeholder only if prompt is empty or just whitespace
            const prompt = this.ai_prompts.positive.trim() ? this.ai_prompts.positive : this.sample_prompts.sample_pos_pro;
            
            // Generate random seed if set to -1
            const final_seed = this.ai_settings.seed === -1 ? this.generate_random_seed() : this.ai_settings.seed;
            
            return {
                user_id: 'frontend_dummy', // Not used - backend gets real ID from session
                prompt: prompt,
                negative_prompt: this.ai_prompts.negative,
                width: this.ai_settings.width,
                height: this.ai_settings.height,
                steps: this.ai_settings.steps,
                cfg_scale: this.ai_settings.cfg_scale,
                sampler: this.ai_settings.sampler,
                scheduler: this.ai_settings.scheduler,
                hires_denoise: this.ai_settings.hires_denoise,
                model: this.ai_settings.model,
                seed: final_seed,
                use_enhanced_prompt: this.ai_settings.use_enhanced_prompt,
                use_mikomic_negative: this.ai_settings.use_mikomic_negative,
                style: this.ai_settings.style,
                reinforce_anime: this.ai_settings.reinforce_anime,
                reinforce_background: this.ai_settings.reinforce_background,
                use_hires_fix: this.ai_settings.use_hires_fix,
                use_prompt_hand_fixer: this.ai_settings.use_prompt_hand_fixer,
                use_same_seed_for_hires: this.ai_settings.use_same_seed_for_hires
            };
        },

        /**
         * Call AI generation API
         */
        async call_ai_generation_api(params) {
            try {
                // Validate prompt lengths before sending (security + UX)
                if (params.params) {
                    const positive_length = (params.params.prompt || '').length;
                    const negative_length = (params.params.negative_prompt || '').length;
                    
                    if (positive_length > 2000 || negative_length > 1000) {
                        this.show_floating_message(this.get_t('ai.prompt_too_long'), 'error');
                        throw new Error('Prompt length exceeds maximum allowed');
                    }
                }
                
                const form_data = new FormData();
                form_data.append('request', JSON.stringify(params));
                
                const response = await axios.post('./ai_api/ai_generation.php', form_data, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                
                //console.log('AI API response:', response.data);
                return response.data;
            } catch (error) {
                console.error('AI API call failed:', error);
                throw new Error(`AI API error: ${error.message}`);
            }
        },

        /**
         * Accept AI generated image and insert into selected panel
         */
        async accept_ai_image() {
            if (!this.ai_preview_image || !this.selected_panel) {
                this.show_floating_message(this.get_t('ai.select_panel_first'), 'warning');
                return;
            }
            
            // Check premium limit for layers EXACTLY like load_image_into_panel()
            const current_layer_count = this.get_panel_layer_count(this.selected_panel);
            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                return;
            }
            
            // Deselect current object EXACTLY like load_image_into_panel()
            this.deselect_object();
            
            try {
                // Convert URL to base64 data like when loading from file system
                const base64_data = await this.url_to_base64(this.ai_preview_image);
                this.create_image_in_panel(base64_data, this.selected_panel, false);
                
                // DO NOT clear preview - preserve generated image for potential reuse
            } catch (error) {
                console.error('Error converting AI image to base64:', error);
                this.show_floating_message(this.get_t('ai.error_accepting_image'), 'error');
            }
        },

        /**
         * Load image without CORS (for non-tainted canvas operations)
         */
        async load_image_without_cors(url) {
            return new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('Image load timeout after 10 seconds'));
                }, 10000);

                const img = new Image();
                
                img.onload = () => {
                    clearTimeout(timeout);
                    resolve(img);
                };
                
                img.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error('Failed to load image without CORS'));
                };
                
                const separator = url.includes('?') ? '&' : '?';
                img.src = url + separator + '_nc=' + Date.now();
            });
        },

        /**
         * Unified image loading with CORS retry logic
         */
        async load_image_with_cors_retry(url, max_attempts = 2, attempt_count = 1) {
            return new Promise((resolve, reject) => {
                // Prevent infinite loops with attempt counter
                if (attempt_count > max_attempts) {
                    console.error('Max retry attempts exceeded for URL:', url);
                    reject(new Error(`Failed to load image after ${max_attempts} attempts`));
                    return;
                }

                // Timeout protection - 10 seconds max per attempt
                const timeout = setTimeout(() => {
                    console.error('Timeout loading image:', url, 'attempt:', attempt_count);
                    reject(new Error(`Image load timeout after 10 seconds (attempt ${attempt_count})`));
                }, 10000);

                const img = new Image();
                
                // Configure crossOrigin for first attempt only (R2 images or external domains)
                if (attempt_count === 1 && (url.includes('images.mikomic.com') || !url.includes(window.location.hostname))) {
                    img.crossOrigin = 'anonymous';
                }
                
                img.onload = () => {
                    clearTimeout(timeout);
                    resolve(img);
                };
                
                img.onerror = () => {
                    clearTimeout(timeout);
                    
                    // Retry only on first attempt and only if CORS was used
                    if (attempt_count === 1 && img.crossOrigin) {
                        console.log(`CORS failed (attempt ${attempt_count}), retrying without crossOrigin for:`, url);
                        // Recursive call with incremented attempt counter
                        this.load_image_with_cors_retry(url, max_attempts, attempt_count + 1)
                            .then(resolve)
                            .catch(reject);
                    } else {
                        reject(new Error(`Failed to load image from URL (attempt ${attempt_count}/${max_attempts})`));
                    }
                };
                
                img.src = url;
            });
        },

        /**
         * Convert URL to base64 data using backend proxy for external URLs
         */
        async url_to_base64(url) {
            try {
                const is_external = url.includes('images.mikomic.com') || url.startsWith('http');
                
                if (is_external) {
                    try {
                        const form_data = new FormData();
                        form_data.append('request', 'proxy_image_to_base64');
                        form_data.append('url', url);
                        
                        const response = await axios.post('ajax.php', form_data);
                        
                        if (response.data.success) {
                            return response.data.data;
                        } else {
                            console.warn('Backend proxy failed:', response.data.message);
                        }
                    } catch (proxy_error) {
                        console.warn('Backend proxy error, trying fallback:', proxy_error);
                    }
                }
                
                const img = await this.load_image_with_cors_retry(url);
                
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                
                ctx.drawImage(img, 0, 0);
                
                try {
                    return canvas.toDataURL('image/webp', 0.9);
                } catch (security_error) {
                    console.warn('Canvas tainted, trying without CORS:', url);
                    const clean_img = await this.load_image_without_cors(url);
                    
                    const clean_canvas = document.createElement('canvas');
                    const clean_ctx = clean_canvas.getContext('2d');
                    clean_canvas.width = clean_img.naturalWidth;
                    clean_canvas.height = clean_img.naturalHeight;
                    clean_ctx.drawImage(clean_img, 0, 0);
                    
                    return clean_canvas.toDataURL('image/webp', 0.9);
                }
            } catch (error) {
                console.error('Error converting URL to base64:', error);
                throw error;
            }
        },

        /**
         * Create image in panel from existing DOM element
         */
        create_image_from_element(img_element, panel) {
            // Calculate polygon bounds for panel dimensions
            const bounds = this.calculate_polygon_bounds(panel.points);
            const content_width = Math.max(bounds.width, 50);
            const content_height = Math.max(bounds.height, 50);
            const center_x = bounds.min_x + bounds.width / 2;
            const center_y = bounds.min_y + bounds.height / 2;
            
            // Calculate scale to fit image properly
            const scale_x = Math.max(content_width / img_element.naturalWidth, 0.01);
            const scale_y = Math.max(content_height / img_element.naturalHeight, 0.01);
            const scale = Math.min(scale_x, scale_y, 1);
            
            // Use the unified function for layer name
            const layer_name = this.get_next_layer_name(panel);
            
            // Create Konva image using existing element
            const konva_image_raw = new Konva.Image({
                image: img_element,
                x: center_x,
                y: center_y,
                offset: {
                    x: img_element.naturalWidth / 2,
                    y: img_element.naturalHeight / 2
                },
                scaleX: scale,
                scaleY: scale,
                draggable: this.active_tool === 'select',
                name: 'panel_image',
                id: 'image_' + Date.now(),
                listening: true,
                globalCompositeOperation: 'normal',
                opacity: 1
            });

            const konva_image = markRaw(konva_image_raw);

            konva_image.setAttr('src_data', img_element.src);
            konva_image.setAttr('layer_name', layer_name);
            konva_image.setAttr('locked', false);
            konva_image.setAttr('object_type', 'image');
            
            // Add image to panel content group
            panel.content_group.add(konva_image);
            
            // Select the newly added image
            if (this.active_tool === 'select') {
                this.select_object(konva_image);
            }
            
            // Create layer object
            this.selected_layer = {
                id: konva_image.id(),
                name: layer_name,
                node: konva_image,
                visible: true,
                locked: false
            };
            
            // Draw the layer and record in history
            this.panels_layer.draw();
            this.push_to_history('add_image');
        },

        /**
         * Handle drag start for AI preview image
         */
        handle_ai_preview_dragstart(e) {
            if (!this.ai_preview_image) {
                e.preventDefault();
                return;
            }

            // Set drag effect and data
            e.dataTransfer.effectAllowed = 'copy';
            e.dataTransfer.setData('text/ai-preview-url', this.ai_preview_image);
            
            // Create drag image preview
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 100;
            canvas.height = 100;
            
            // Set semi-transparent background
            ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
            ctx.fillRect(0, 0, 100, 100);
            ctx.fillStyle = '#ffffff';
            ctx.font = '12px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('AI Image', 50, 50);
            
            e.dataTransfer.setDragImage(canvas, 50, 50);
        },

        /**
         * Discard AI generated image with confirmation
         */
        discard_ai_image() {
            // Only allow discard for generated images, not sample.webp
            if (!this.ai_preview_image || !this.is_generated_image) return;
            
            // Show confirmation popup before deleting
            this.show_confirmation_popup(
                this.get_t('ai.discard_confirm_title'),
                this.get_t('ai.discard_confirm_message'),
                async () => {
                    await this.confirm_discard_ai_image();
                }
            );
        },

        /**
         * Actually discard the AI image after confirmation
         */
        async confirm_discard_ai_image() {
            if (!this.ai_preview_image || !this.is_generated_image) return;
            
            try {
                // Call API to delete image from R2 and database
                const response = await this.call_ai_generation_api({
                    action: 'delete_image',
                    image_url: this.ai_preview_image
                });
                
                if (response.success) {
                    // Clear preview and show success message
                    this.ai_preview_image = './img/ai/sample.webp';
                    this.is_generated_image = false;
                    
                    // Remove from local history if present (without R2 refresh)
                    if (this.show_generation_history) {
                        const image_index = this.generation_history.findIndex(img => img.url === response.deleted_url || this.ai_preview_image);
                        if (image_index !== -1) {
                            this.generation_history.splice(image_index, 1);
                            this.history_total_images = Math.max(0, this.history_total_images - 1);
                        }
                    }
                    
                    this.show_floating_message(this.get_t('ai.image_deleted'), 'success');
                } else {
                    console.error('Failed to delete image:', response.error);
                    this.show_floating_message(this.get_t('ai.error_deleting_image'), 'error');
                }
            } catch (error) {
                console.error('Error deleting AI image:', error);
                this.show_floating_message(this.get_t('ai.error_deleting_image'), 'error');
            }
        },
        
        /**
         * Show AI image in fullscreen modal
         */
        show_ai_image_fullscreen(image_url = null, is_history = false, history_index = -1) {
            const display_url = image_url || this.ai_preview_image;
            if (!display_url) return;
            
            let current_index = history_index;
            let current_url = display_url;
            
            // Create fullscreen overlay
            const overlay = document.createElement('div');
            overlay.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: rgba(0, 0, 0, 0.9);
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                z-index: 50000;
                cursor: pointer;
            `;
            
            // Create container for image and actions
            const container = document.createElement('div');
            container.style.cssText = `
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 20px;
                cursor: default;
                position: relative;
            `;
            
            // Create image element
            const img = document.createElement('img');
            img.src = current_url;
            img.style.cssText = `
                max-width: 90vw;
                max-height: 80vh;
                object-fit: contain;
                border-radius: 8px;
                box-shadow: 0 10px 30px rgba(0,0,0,0.5);
            `;
            
            // Function to update image and buttons
            const update_image = async (index) => {
                // Check if we need to load more images
                if (index >= this.generation_history.length - 2 && this.history_has_more && !this.history_loading) {
                    await this.load_generation_history();
                }
                
                // Check if index is out of bounds or image is placeholder
                if (index < 0 || index >= this.generation_history.length) return;
                
                const new_image = this.generation_history[index];
                
                // Skip if image is a placeholder (no url or url is undefined)
                if (!new_image || !new_image.url) {
                    // If we have more to load, try loading and then retry
                    if (this.history_has_more && !this.history_loading) {
                        await this.load_generation_history();
                        // Retry with the same index after loading
                        if (index < this.generation_history.length && this.generation_history[index].url) {
                            await update_image(index);
                        }
                    }
                    return;
                }
                
                current_index = index;
                current_url = new_image.url;
                img.src = current_url;
                
                // Update button states
                if (is_history) {
                    // Update action buttons for new image
                    const button_container = container.querySelector('.action-buttons');
                    if (button_container) {
                        // Clear existing buttons
                        button_container.innerHTML = '';
                        
                        // Recreate buttons for new image
                        create_action_buttons(new_image, button_container);
                        
                        // Process Lucide icons for new buttons
                        this.$nextTick(() => {
                            if (window.lucide) {
                                lucide.createIcons();
                            }
                        });
                        
                        // Update navigation buttons based on actual availability
                        const can_go_prev = index > 0;
                        const can_go_next = index < this.generation_history.length - 1 || this.history_has_more;
                        
                        prev_btn.style.opacity = can_go_prev ? '1' : '0.3';
                        prev_btn.style.cursor = can_go_prev ? 'pointer' : 'default';
                        next_btn.style.opacity = can_go_next ? '1' : '0.3';
                        next_btn.style.cursor = can_go_next ? 'pointer' : 'default';
                    }
                }
            };
            
            container.appendChild(img);
            
            // Add navigation arrows if viewing history
            let prev_btn, next_btn;
            if (is_history && this.generation_history.length > 1 && current_index >= 0) {
                // Previous arrow
                prev_btn = document.createElement('button');
                prev_btn.innerHTML = `<i data-lucide="chevron-left" class="w-6 h-6"></i>`;
                prev_btn.style.cssText = `
                    position: absolute;
                    left: -80px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 50px;
                    height: 50px;
                    background: rgba(0, 0, 0, 0.7);
                    color: white;
                    border: none;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                    opacity: ${current_index > 0 ? '1' : '0.3'};
                `;
                prev_btn.addEventListener('mouseenter', () => {
                    if (current_index > 0) prev_btn.style.background = 'rgba(0, 0, 0, 0.9)';
                });
                prev_btn.addEventListener('mouseleave', () => prev_btn.style.background = 'rgba(0, 0, 0, 0.7)');
                prev_btn.addEventListener('click', async (e) => {
                    e.stopPropagation();
                    if (current_index > 0) {
                        await update_image(current_index - 1);
                    }
                });
                
                // Next arrow
                next_btn = document.createElement('button');
                next_btn.innerHTML = `<i data-lucide="chevron-right" class="w-6 h-6"></i>`;
                next_btn.style.cssText = `
                    position: absolute;
                    right: -80px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 50px;
                    height: 50px;
                    background: rgba(0, 0, 0, 0.7);
                    color: white;
                    border: none;
                    border-radius: 50%;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                    opacity: ${current_index < this.generation_history.length - 1 ? '1' : '0.3'};
                `;
                next_btn.addEventListener('mouseenter', () => {
                    const can_navigate = current_index < this.generation_history.length - 1 || this.history_has_more;
                    if (can_navigate) next_btn.style.background = 'rgba(0, 0, 0, 0.9)';
                });
                next_btn.addEventListener('mouseleave', () => next_btn.style.background = 'rgba(0, 0, 0, 0.7)');
                next_btn.addEventListener('click', async (e) => {
                    e.stopPropagation();
                    // Allow navigation if we have next image or can load more
                    if (current_index < this.generation_history.length - 1 || this.history_has_more) {
                        await update_image(current_index + 1);
                    }
                });
                
                container.appendChild(prev_btn);
                container.appendChild(next_btn);
            }
            
            // Function to create action buttons
            const create_action_buttons = (history_image, button_container) => {
                // Insert button (only for comic mode)
                if (this.comic_mode) {
                    const insert_btn = document.createElement('button');
                    insert_btn.innerHTML = `<i data-lucide="plus" class="w-4 h-4 mr-2"></i>${this.get_t('ai.insert_image')}`;
                    insert_btn.style.cssText = `
                        padding: 10px 20px;
                        background: #10b981;
                        color: white;
                        border: none;
                        border-radius: 6px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        transition: background-color 0.2s;
                    `;
                    insert_btn.addEventListener('mouseenter', () => insert_btn.style.background = '#059669');
                    insert_btn.addEventListener('mouseleave', () => insert_btn.style.background = '#10b981');
                    insert_btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        this.insert_history_image(history_image);
                        document.body.removeChild(overlay);
                        document.removeEventListener('keydown', handle_keydown);
                    });
                    button_container.appendChild(insert_btn);
                }
                
                // Download button
                const download_btn = document.createElement('button');
                download_btn.innerHTML = `<i data-lucide="download" class="w-4 h-4 mr-2"></i>${this.get_t('ai.download')}`;
                download_btn.style.cssText = `
                    padding: 10px 20px;
                    background: #8b5cf6;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    transition: background-color 0.2s;
                `;
                download_btn.addEventListener('mouseenter', () => download_btn.style.background = '#7c3aed');
                download_btn.addEventListener('mouseleave', () => download_btn.style.background = '#8b5cf6');
                download_btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.download_history_image(history_image);
                });
                
                // Delete button
                const delete_btn = document.createElement('button');
                delete_btn.innerHTML = `<i data-lucide="trash-2" class="w-4 h-4 mr-2"></i>${this.get_t('ai.delete_image')}`;
                delete_btn.style.cssText = `
                    padding: 10px 20px;
                    background: #ef4444;
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    transition: background-color 0.2s;
                `;
                delete_btn.addEventListener('mouseenter', () => delete_btn.style.background = '#dc2626');
                delete_btn.addEventListener('mouseleave', () => delete_btn.style.background = '#ef4444');
                delete_btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.confirm_delete_history_image(history_image);
                    document.body.removeChild(overlay);
                    document.removeEventListener('keydown', handle_keydown);
                });
                
                button_container.appendChild(download_btn);
                button_container.appendChild(delete_btn);
            };
            
            // Add action buttons if viewing history image
            if (is_history) {
                const button_container = document.createElement('div');
                button_container.className = 'action-buttons';
                button_container.style.cssText = `
                    display: flex;
                    gap: 12px;
                    align-items: center;
                `;
                
                // Find the history image object
                const history_image = this.generation_history.find(image => image.url === current_url);
                
                if (history_image) {
                    create_action_buttons(history_image, button_container);
                }
                
                container.appendChild(button_container);
            }
            
            // Handle keyboard navigation
            const handle_keydown = async (e) => {
                if (e.key === 'Escape') {
                    document.body.removeChild(overlay);
                    document.removeEventListener('keydown', handle_keydown);
                } else if (is_history && current_index >= 0) {
                    if (e.key === 'ArrowLeft' && current_index > 0) {
                        e.preventDefault();
                        await update_image(current_index - 1);
                    } else if (e.key === 'ArrowRight' && (current_index < this.generation_history.length - 1 || this.history_has_more)) {
                        e.preventDefault();
                        await update_image(current_index + 1);
                    }
                }
            };
            document.addEventListener('keydown', handle_keydown);
            
            // Close on overlay click
            overlay.addEventListener('click', (e) => {
                if (e.target === overlay) {
                    document.body.removeChild(overlay);
                    document.removeEventListener('keydown', handle_keydown);
                }
            });
            
            overlay.appendChild(container);
            document.body.appendChild(overlay);
            
            // Process Lucide icons after everything is added to DOM
            this.$nextTick(() => {
                if (window.lucide) {
                    lucide.createIcons();
                }
            });
            
        },

        /**
         * Download AI generated image - Opens in new tab or triggers download
         */
        download_ai_image() {
            if (!this.ai_preview_image) return;
            
            // Create a link and trigger download
            const link = document.createElement('a');
            link.href = this.ai_preview_image;
            link.download = `mikomic_ai_${Date.now()}.webp`;
            link.target = '_blank'; // Open in new tab as fallback
            
            // Add to DOM temporarily
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            //console.log('AI image download initiated:', this.ai_preview_image);
        },

        /**
         * Get generation cost in credits based on parameters
         */
        get_generation_cost() {
            return this.calculate_dynamic_credits(
                this.ai_settings.width,
                this.ai_settings.height, 
                this.ai_settings.steps,
                this.ai_settings.use_hires_fix
            );
        },

        calculate_dynamic_credits(width, height, steps, use_hires) {
            const BASE_PIXELS = 1024 * 1536;
            const BASE_STEPS = 23;
            const MINIMUM_CREDITS = 52;
            const BASE_CREDITS_WITH_HIRES = 100;
            const BASE_CREDITS_WITHOUT_HIRES = 80;
            
            const current_pixels = width * height;
            let resolution_factor = current_pixels / BASE_PIXELS;
            if (resolution_factor > 1.0) {
                resolution_factor = 1.0 + ((resolution_factor - 1.0) * 3.25);
            }
            const steps_factor = this.calculate_steps_factor(steps);
            const target_base = use_hires ? BASE_CREDITS_WITH_HIRES : BASE_CREDITS_WITHOUT_HIRES;
            const scale_factor = resolution_factor * steps_factor;
            const credits = MINIMUM_CREDITS + ((target_base - MINIMUM_CREDITS) * scale_factor);
            
            return Math.max(Math.round(credits), MINIMUM_CREDITS);
        },

        /**
         * Calculate steps factor with pricing curve
         */
        calculate_steps_factor(steps) {
            if (steps < 23) {
                return 0.62 + (steps / 23) * 0.38;
            } else if (steps > 23) {
                return 1.0 + ((steps - 23) / 23) * 0.82;
            }
            return 1.0;
        },

        /**
         * Show floating discrete message instead of modal
         * IMPLEMENTATION of the function I invented like an idiot
         */
        show_floating_message(message, type = 'info') {
            // Create floating message element
            const messageEl = document.createElement('div');
            messageEl.className = `floating-message floating-message-${type}`;
            messageEl.textContent = message;
            
            // Style the message
            Object.assign(messageEl.style, {
                position: 'fixed',
                top: '20px',
                right: '20px',
                padding: '12px 16px',
                borderRadius: '6px',
                color: 'white',
                fontWeight: 'bold',
                zIndex: '10000',
                maxWidth: '300px',
                wordWrap: 'break-word',
                fontSize: '14px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                transform: 'translateX(400px)',
                transition: 'all 0.3s ease'
            });
            
            // Set background color based on type
            switch(type) {
                case 'error':
                    messageEl.style.backgroundColor = '#dc2626';
                    break;
                case 'success':
                    messageEl.style.backgroundColor = '#16a34a';
                    break;
                case 'warning':
                    messageEl.style.backgroundColor = '#d97706';
                    break;
                case 'info':
                default:
                    messageEl.style.backgroundColor = '#2563eb';
                    break;
            }
            
            // Add to body
            document.body.appendChild(messageEl);
            
            // Animate in
            setTimeout(() => {
                messageEl.style.transform = 'translateX(0)';
            }, 10);
            
            // Auto remove after 4 seconds
            setTimeout(() => {
                messageEl.style.transform = 'translateX(400px)';
                setTimeout(() => {
                    if (messageEl.parentNode) {
                        messageEl.parentNode.removeChild(messageEl);
                    }
                }, 300);
            }, 4000);
            
            //console.log(`Floating message (${type}):`, message);
        },

        /**
         * Get available AI credits
         */
        get_available_ai_credits() {
            //console.log('Available AI credits:', this.ai_credits);
            return this.ai_credits || 0;
        },

        /**
         * Create image element from URL using unified loading with CORS retry
         */
        async create_image_from_url(url) {
            return await this.load_image_with_cors_retry(url);
        },

        /**
         * Close all right panels except AI
         */
        close_all_right_panels() {
            // This will be implemented to close other panels when AI is active
        },

        /**
         * Set size preset with proper orientation handling
         */
        set_size_preset(ratio) {            
            switch(ratio) {
                case '16:9':
                    if (this.presets_rotated) {
                        this.ai_settings.width = 864;
                        this.ai_settings.height = 1536;
                    } else {
                        this.ai_settings.width = 1536;
                        this.ai_settings.height = 864;
                    }
                    break;
                case '2:3':
                    if (this.presets_rotated) {
                        this.ai_settings.width = 1024;
                        this.ai_settings.height = 1536;
                    } else {
                        this.ai_settings.width = 1536;
                        this.ai_settings.height = 1024;
                    }
                    break;
                case '1:1':
                    this.ai_settings.width = 1024;
                    this.ai_settings.height = 1024;
                    break;
            }
        },

        /**
         * Swap width and height dimensions
         */
        swap_dimensions() {
            const temp = this.ai_settings.width;
            this.ai_settings.width = this.ai_settings.height;
            this.ai_settings.height = temp;
            
            // Toggle presets rotation
            this.presets_rotated = !this.presets_rotated;
        },

        /**
         * Validate dimension input
         */
        validate_dimension(dimension, event) {
            let value = parseInt(event.target.value);
            if (isNaN(value) || value > 1536) {
                value = Math.min(value || 1536, 1536);
            }
            if (value < 768) {
                value = 768;
            }
            this.ai_settings[dimension] = value;
            event.target.value = value;
        },

        /**
         * Show model info modal
         */
        show_model_info(model_name) {
            const model = this.available_models.find(m => m.name === model_name);
            if (!model) {
                console.error('Model not found:', model_name);
                return;
            }
            
            this.current_model_info = model;
            this.current_demo_image_index = 0;
            this.show_model_info_modal = true;
        },
        
        /**
         * Close model info modal
         */
        close_model_info_modal() {
            this.show_model_info_modal = false;
            this.current_model_info = null;
            this.current_demo_image_index = 0;
        },
        
        /**
         * Navigate demo images in model info modal
         */
        navigate_demo_image(direction) {
            if (!this.current_model_info || !this.current_model_info.demo_images) return;
            
            const total = this.current_model_info.demo_images.length;
            if (direction === 'prev' && this.current_demo_image_index > 0) {
                this.current_demo_image_index--;
            } else if (direction === 'next' && this.current_demo_image_index < total - 1) {
                this.current_demo_image_index++;
            }
        },

        open_model_info_lightbox(index) {
            this.model_info_lightbox_index = index;
            this.model_info_lightbox_active = true;
        },
        
        close_model_info_lightbox() {
            this.model_info_lightbox_active = false;
        },
        
        navigate_lightbox(direction) {
             if (!this.current_model_info || !this.current_model_info.demo_images) return;
             
             const total = this.current_model_info.demo_images.length;
             if (direction === 'prev') {
                 this.model_info_lightbox_index = (this.model_info_lightbox_index - 1 + total) % total;
             } else {
                 this.model_info_lightbox_index = (this.model_info_lightbox_index + 1) % total;
             }
        },

        /**
         * Scroll the model info carousel
         */
        scroll_info_carousel(direction) {
            const container = this.$refs.model_info_carousel;
            if (!container) return;
            
            // Use container width for better responsive behavior
            const scroll_amount = container.clientWidth;
            
            if (direction === 'prev') {
                container.scrollBy({ left: -scroll_amount, behavior: 'smooth' });
            } else {
                container.scrollBy({ left: scroll_amount, behavior: 'smooth' });
            }
        },

        /**
         * Check for existing generation state when page loads (multiple tabs support)
         */
        async check_existing_generation_state() {
            try {
                const response = await this.call_ai_generation_api({
                    action: 'check_queue_status'
                });
                
                if (response.success) {
                    // Use processed data from backend - no calculations needed
                    const user_is_processing = response.user_is_processing;
                    const user_processing_timestamp = response.user_processing_timestamp;
                    const user_queue_timestamp = response.user_queue_timestamp;
                    const user_queue_position = response.user_queue_position;
                    
                    // Check if current user is being processed
                    if (user_is_processing && user_processing_timestamp) {
                        console.log('Found existing generation being processed for this user');
                        this.ai_generating = true;
                        this.ai_queue_status = 'processing';
                        this.ai_queue_position = 0;
                        this.my_request_timestamp = user_processing_timestamp;
                        this.generation_start_time = Date.now(); // Reset timer for processing phase
                        this.start_timestamp_monitoring();
                        return;
                    }
                    
                    // Check if user is in queue
                    if (user_queue_timestamp && user_queue_position > 0) {
                        console.log(`Found existing generation in queue for this user, position: ${user_queue_position}`);
                        this.ai_generating = true;
                        this.ai_queue_status = 'queued';
                        this.ai_queue_position = user_queue_position;
                        this.my_request_timestamp = user_queue_timestamp;
                        this.generation_start_time = Date.now(); // Set for timeout management
                        this.start_timestamp_monitoring();
                        return;
                    }
                    
                    console.log('No existing generation found for this user');
                } else {
                    console.log('Queue status check failed:', response.error);
                }
            } catch (error) {
                console.error('Error checking existing generation state:', error);
            }
        },

        /**
         * Load generation configuration from server
         */
        async load_generation_config() {
            try {
                const response = await this.call_ai_generation_api({ action: 'get_generation_cost' });
                if (response.success && response.image_credit_cost) {
                    this.image_credit_cost = response.image_credit_cost;
                } else {
                    throw new Error('Failed to load generation cost');
                }
            } catch (error) {
                console.error('Failed to load generation cost:', error);
                this.show_floating_message(this.get_t('ai.failed_to_load_generation_cost'), 'error');
            }
        },

        /**
         * Toggle advanced settings visibility
         */
        toggle_advanced_settings() {
            this.advanced_settings_collapsed = !this.advanced_settings_collapsed;
        },

        /**
         * Generate random seed using same logic as Python script
         */
        generate_random_seed() {
            // Same range as Python: random.randint(1000000000000, 1125899906842620)
            const min = 1000000000000;
            const max = 1125899906842620;
            return Math.floor(Math.random() * (max - min + 1)) + min;
        },

        /**
         * Randomize seed with UI feedback
         */
        randomize_seed() {
            this.ai_settings.seed = -1; // Set to -1 for random generation
        },

        /**
         * Compare two parameter objects for identical generation
         */
        are_params_identical(params1, params2) {
            if (!params1 || !params2) return false;
            
            // Special case: if both seeds are -1 (random), generate random seeds for both and compare
            if (params1.seed === -1 && params2.seed === -1) {
                params1.seed = this.generate_random_seed();
                params2.seed = this.generate_random_seed();
                // Now they will have different random seeds, so continue with comparison
            }
            
            // Compare all critical parameters that affect generation
            const critical_keys = [
                'prompt', 'negative_prompt', 'width', 'height', 'steps', 
                'cfg_scale', 'sampler', 'scheduler', 'hires_denoise', 
                'model', 'seed', 'style', 'reinforce_anime', 'reinforce_background',
                'use_enhanced_prompt', 'use_mikomic_negative', 'use_hires_fix','use_prompt_hand_fixer','use_same_seed_for_hires'
            ];
            
            return critical_keys.every(key => params1[key] === params2[key]);
        },

        /**
         * Handle file drop for metadata loading
         */
        handle_ai_metadata_drop(event) {
            event.preventDefault();
            event.stopPropagation();
            this.drop_zone_active = false;
            
            // Check if it's an AI image URL (from drag)
            const ai_image_url = event.dataTransfer?.getData('text/plain');
            if (ai_image_url && (ai_image_url.includes('cloudflare') || ai_image_url.startsWith('http'))) {
                // Load metadata directly from URL
                this.load_metadata_from_url(ai_image_url);
                return;
            }
            
            // Handle file drops
            const files = event.dataTransfer?.files || event.target.files;
            if (files && files.length > 0) {
                this.load_image_metadata(files[0]);
            }
        },

        /**
         * Load metadata directly from image URL using unified image loading
         */
        async load_metadata_from_url(url) {
            try {
                // Use unified image loading with CORS retry
                const img = await this.load_image_with_cors_retry(url);
                
                try {
                    // Extract generation parameters directly from the image
                    const has_metadata = await this.extract_generation_parameters(url, img);
                    
                    if (has_metadata) {
                        // Set the image as preview when metadata is successfully loaded
                        this.ai_preview_image = url;
                        this.is_generated_image = true;
                        this.show_floating_message(this.get_t('ai.metadata_loaded'), 'success');
                    } else {
                        this.show_floating_message(this.get_t('ai.metadata_load_error'), 'warning');
                    }
                } catch (error) {
                    console.error('Error extracting metadata from URL:', error);
                    this.show_floating_message(this.get_t('ai.metadata_load_error'), 'error');
                }
            } catch (error) {
                console.error('Error loading image for metadata from URL:', error);
                this.show_floating_message(this.get_t('ai.metadata_load_error'), 'error');
            }
        },

        /**
         * Handle drag events for drop zone
         */
        handle_ai_drag_enter(event) {
            event.preventDefault();
            event.stopPropagation();
            this.drop_zone_active = true;
        },

        handle_ai_drag_leave(event) {
            event.preventDefault();
            event.stopPropagation();
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.drop_zone_active = false;
            }
        },

        handle_ai_drag_over(event) {
            event.preventDefault();
            event.stopPropagation();
        },

        /**
         * Load metadata from image file
         */
        async load_image_metadata(file) {
            try {
                // Create file reader to extract EXIF/metadata
                const reader = new FileReader();
                
                reader.onload = (e) => {
                    const img = new Image();
                    img.onload = async () => {
                        try {
                            // Try to extract generation parameters from EXIF
                            const has_metadata = await this.extract_generation_parameters(file.name, img);
                            
                            if (has_metadata) {
                                // Set the image as preview when metadata is successfully loaded
                                this.ai_preview_image = img.src;
                                this.is_generated_image = true; // Mark as generated image from metadata
                                this.show_floating_message(this.get_t('ai.metadata_loaded'), 'success');
                            } else {
                                this.show_floating_message(this.get_t('ai.metadata_load_error'), 'warning');
                            }
                        } catch (error) {
                            console.error('Error extracting metadata:', error);
                            this.show_floating_message(this.get_t('ai.metadata_load_error'), 'error');
                        }
                    };
                    img.src = e.target.result;
                };
                
                reader.readAsDataURL(file);
            } catch (error) {
                console.error('Error reading file:', error);
                this.show_floating_message(this.get_t('ai.metadata_load_error'), 'error');
            }
        },

        /**
         * Extract generation parameters from image
         */
        async extract_generation_parameters(filename, img) {
            
            if (!img) return;
            
            try {
                // Convert image source (base64 or URL) directly to ArrayBuffer
                let array_buffer;
                
                if (img.src.startsWith('data:')) {
                    // Extract from base64 data URL
                    const base64_data = img.src.split(',')[1];
                    const binary_string = atob(base64_data);
                    const bytes = new Uint8Array(binary_string.length);
                    for (let i = 0; i < binary_string.length; i++) {
                        bytes[i] = binary_string.charCodeAt(i);
                    }
                    array_buffer = bytes.buffer;
                } else {
                    // Clean URL by removing any existing query parameters
                    const clean_url = img.src.split('?')[0];
                    
                    // Check if it's an external image that might have CORS issues
                    if (clean_url.includes('images.mikomic.com')) {
                        // Use proxy to avoid CORS issues
                        const proxy_url = `./image_proxy.php?url=${encodeURIComponent(clean_url)}`;
                        const response = await fetch(proxy_url, {
                            cache: 'no-cache'
                        });
                        
                        if (!response.ok) {
                            throw new Error(`Proxy fetch failed with status ${response.status}`);
                        }
                        
                        array_buffer = await response.arrayBuffer();
                    } else {
                        // Direct fetch for local or other URLs
                        const response = await fetch(clean_url, {
                            mode: 'cors',
                            cache: 'no-cache'
                        });
                        
                        if (!response.ok) {
                            throw new Error(`Fetch failed with status ${response.status}`);
                        }
                        
                        array_buffer = await response.arrayBuffer();
                    }
                }
                
                // Look for EXIF data in WebP format
                const params = this.extract_mikomic_exif(new DataView(array_buffer));
                
                if (Object.keys(params).length > 0) {
                    this.apply_extracted_parameters(params);
                    return true; // Successfully loaded metadata
                } else {
                    console.log('No Mikomic metadata found in image');
                    return false; // No metadata found
                }
                
            } catch (error) {
                console.error('Error extracting parameters:', error);
                return false;
            }
        },

        extract_mikomic_exif(data_view) {
            try {
                // Look for EXIF marker in image data
                let params = {};
                
                // Convert data_view to string to search for UserParams marker
                const uint8_array = new Uint8Array(data_view.buffer);
                const text_data = new TextDecoder('utf-8', { ignoreBOM: true, fatal: false }).decode(uint8_array);
                
                // Search for UserParams: marker (from mikomic_save_webp.py line 103)
                const user_params_index = text_data.indexOf('UserParams:');
                if (user_params_index !== -1) {
                    
                    // Extract JSON data after UserParams:
                    let json_start = user_params_index + 11; // Length of 'UserParams:'
                    let json_string = '';
                    let brace_count = 0;
                    let in_json = false;
                    
                    for (let i = json_start; i < text_data.length; i++) {
                        const char = text_data.charAt(i);
                        
                        if (char === '{') {
                            brace_count++;
                            in_json = true;
                            json_string += char;
                        } else if (char === '}' && in_json) {
                            brace_count--;
                            json_string += char;
                            if (brace_count === 0) break;
                        } else if (in_json) {
                            json_string += char;
                        }
                    }
                    
                    if (json_string) {
                        try {
                            params = JSON.parse(json_string);
                        } catch (parse_error) {
                            console.error('Error parsing metadata JSON:', parse_error, 'JSON string:', json_string);
                        }
                    }
                }
                
                return params;
            } catch (error) {
                console.error('Error reading EXIF data:', error);
                return {};
            }
        },

        apply_extracted_parameters(params) {
            try {
                
                // Apply CORE generation parameters
                if (params.seed !== undefined) this.ai_settings.seed = params.seed;
                if (params.steps !== undefined) this.ai_settings.steps = params.steps;
                if (params.cfg_scale !== undefined) this.ai_settings.cfg_scale = params.cfg_scale;
                if (params.width !== undefined) this.ai_settings.width = params.width;
                if (params.height !== undefined) this.ai_settings.height = params.height;
                if (params.denoise !== undefined) this.ai_settings.denoise = params.denoise;
                
                // Apply STYLE and ENHANCEMENT parameters
                if (params.style !== undefined) {
                    this.ai_settings.style = params.style;
                    // Update fake model selection based on style
                    if (!this.show_legacy_models) {
                        this.current_variant = params.style;
                    }
                }
                if (params.use_enhanced_prompt !== undefined) this.ai_settings.use_enhanced_prompt = params.use_enhanced_prompt;
                if (params.use_mikomic_negative !== undefined) this.ai_settings.use_mikomic_negative = params.use_mikomic_negative;
                if (params.reinforce_anime !== undefined) this.ai_settings.reinforce_anime = params.reinforce_anime;
                if (params.reinforce_background !== undefined) this.ai_settings.reinforce_background = params.reinforce_background;
                
                // Apply HIRES FIX parameters
                if (params.use_hires_fix !== undefined) this.ai_settings.use_hires_fix = params.use_hires_fix;
                if (params.hires_denoise !== undefined) this.ai_settings.hires_denoise = params.hires_denoise;
                if (params.use_same_seed_for_hires !== undefined) this.ai_settings.use_same_seed_for_hires = params.use_same_seed_for_hires;

                // Apply PROMPT FIXERS
                if (params.use_prompt_hand_fixer !== undefined) this.ai_settings.use_prompt_hand_fixer = params.use_prompt_hand_fixer;
                
                // Apply SAMPLER and SCHEDULER
                if (params.sampler_name !== undefined) this.ai_settings.sampler = params.sampler_name;
                if (params.sampler !== undefined) this.ai_settings.sampler = params.sampler;
                if (params.scheduler !== undefined) this.ai_settings.scheduler = params.scheduler;
                
                // Apply MODEL (only in legacy mode)
                if (this.show_legacy_models && params.model !== undefined) this.ai_settings.model = params.model;
                if (params.model !== undefined) this.ai_settings.model = params.model;
                
                // Apply PROMPTS
                if (params.prompt !== undefined) this.ai_prompts.positive = params.prompt;
                if (params.negative_prompt !== undefined) this.ai_prompts.negative = params.negative_prompt;
                
                
            } catch (error) {
                console.error('Error applying parameters:', error);
            }
        },

        /**
         * Handle negative prompt logic based on mikomic negative setting
         * Values: 1=custom, 2=general, 3=detailed, 4=custom+general, 5=custom+detailed
         */
        handle_negative_prompt_change() {
            // Clear custom negative prompt only when using pure mikomic negatives (not append modes)
            if (this.ai_settings.use_mikomic_negative === 2 || this.ai_settings.use_mikomic_negative === 3) {
                this.ai_prompts.negative = '';
            }
            // For append modes (4, 5), keep the custom negative prompt
        },

        /**
         * Handle add to general checkbox/button change
         */
        toggle_add_general(event) {
            if (this.comic_mode) {
                // MIKOMIC: Handle checkbox events
                if (event.target.checked) {
                    this.ai_settings.use_mikomic_negative = 4; // Custom + General
                } else {
                    // Go back to pure custom
                    this.ai_settings.use_mikomic_negative = 1;
                }
            } else {
                // MIKOGEN: Handle button toggle
                if (this.ai_settings.use_mikomic_negative === 4) {
                    // Already active, deactivate it (go to pure custom)
                    this.ai_settings.use_mikomic_negative = 1;
                } else {
                    // Not active, activate it (this deactivates detailed if it was active)
                    this.ai_settings.use_mikomic_negative = 4;
                }
            }
        },

        /**
         * Handle add to detailed checkbox/button change
         */
        toggle_add_detailed(event) {
            if (this.comic_mode) {
                // MIKOMIC: Handle checkbox events
                if (event.target.checked) {
                    this.ai_settings.use_mikomic_negative = 5; // Custom + Detailed
                } else {
                    // Go back to pure custom
                    this.ai_settings.use_mikomic_negative = 1;
                }
            } else {
                // MIKOGEN: Handle button toggle
                if (this.ai_settings.use_mikomic_negative === 5) {
                    // Already active, deactivate it (go to pure custom)
                    this.ai_settings.use_mikomic_negative = 1;
                } else {
                    // Not active, activate it (this deactivates general if it was active)
                    this.ai_settings.use_mikomic_negative = 5;
                }
            }
        },

        /**
         * Toggle generation history dropdown
         */
        toggle_generation_history() {
            this.show_generation_history = !this.show_generation_history;
            
            // Load initial images when opening for the first time
            if (this.show_generation_history && !this.history_loaded_from_server) {
                this.load_generation_history(true); // Reset and load from beginning
            }
            
            // Auto-scroll to generation history section when opening
            if (this.show_generation_history) {
                // Add delay to allow DOM expansion and image loading
                setTimeout(() => {
                    const history_section = document.querySelector('#generation-history-section');
                    if (history_section) {
                        history_section.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                        });
                    }
                }, 500); // 500ms delay for DOM updates and initial image loading
            }
        },

        /**
         * Load generation history with lazy loading
         */
        async load_generation_history(reset = false) {
            if (this.history_loading) return;
            
            this.history_loading = true;
            
            try {
                // Reset pagination if requested
                if (reset) {
                    this.history_page = 0;
                    this.generation_history = [];
                    this.history_has_more = true;
                    this.history_total_images = 0;
                    this.history_loaded_from_server = false;
                }

                const response = await this.call_ai_generation_api({
                    action: 'get_user_history',
                    page: this.history_page,
                    per_page: this.history_per_page
                });
                
                if (response.success && response.images) {
                    // Get total images count
                    const total_images = response.total_images || this.generation_history.length;
                    
                    if (reset) {
                        // Create array with real images
                        this.generation_history = [...response.images];
                        
                        // Add 7 placeholders (page + 1) ONLY if total_images > 6
                        if (total_images > 6) {
                            for (let i = 0; i < 7; i++) {
                                this.generation_history.push({ is_placeholder: true, id: `placeholder_${i}` });
                            }
                        }
                    } else {
                        // Remove existing placeholders and append new real images
                        this.generation_history = this.generation_history.filter(img => !img.is_placeholder);
                        this.generation_history.push(...response.images);
                        
                        // Add 7 placeholders ONLY if there are still more images to load
                        if (response.has_more) {
                            for (let i = 0; i < 7; i++) {
                                this.generation_history.push({ is_placeholder: true, id: `placeholder_${this.history_page}_${i}` });
                            }
                        }
                        // If no more images (has_more = false), don't add placeholders - we're done
                    }
                    
                    // Update pagination and has_more flag
                    this.history_page++;
                    this.history_has_more = response.has_more || false;
                    this.history_total_images = total_images;
                    this.history_loaded_from_server = true;
                } else {
                    console.error('Failed to load history:', response.error);
                    this.history_has_more = false;
                    this.history_total_images = 0;
                }
            } catch (error) {
                console.error('Error loading generation history:', error);
            } finally {
                this.history_loading = false;
            }
        },

        /**
         * Handle scroll in history container for lazy loading
         */
        handle_history_scroll(event) {
            // Only load more if we have more content and not currently loading
            if (!this.history_has_more || this.history_loading) return;
            
            const container = event.target;            
            var threshold = 250; // Load more when 250px from bottom (more aggressive)

            if (!this.comic_mode)
            {
                threshold = 700; 
            }
            
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - threshold) {
                this.load_generation_history(); // Load next page
            }
        },

        /**
         * View history image in fullscreen
         */
        view_history_image(image) {
            const image_index = this.generation_history.findIndex(img => img.url === image.url);
            this.show_ai_image_fullscreen(image.url, true, image_index);
        },

        /**
         * Download history image
         */
        download_history_image(image) {
            const link = document.createElement('a');
            link.href = image.url;
            link.download = image.filename || `mikomic_ai_${Date.now()}.webp`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        /**
         * Insert history image into selected panel
         */
        async insert_history_image(image) {
            if (!this.selected_panel) {
                this.show_floating_message(this.get_t('ai.select_panel_first'), 'warning');
                return;
            }
            
            // Check premium limit
            const current_layer_count = this.get_panel_layer_count(this.selected_panel);
            if (!this.check_local_restriction('max_layers_per_panel', current_layer_count)) {
                return;
            }
            
            // Deselect current object
            this.deselect_object();
            
            try {
                // Convert URL to base64 like accept_ai_image does
                const base64_data = await this.url_to_base64(image.url);
                this.create_image_in_panel(base64_data, this.selected_panel, false);
                
                this.show_floating_message(this.get_t('ai.image_accepted'), 'success');
            } catch (error) {
                console.error('Error inserting history image:', error);
                this.show_floating_message(this.get_t('ai.error_accepting_image'), 'error');
            }
        },

        /**
         * Delete history image with confirmation
         */
        confirm_delete_history_image(image) {
            this.show_confirmation_popup(
                this.get_t('ai.discard_confirm_title'),
                this.get_t('ai.discard_confirm_message'),
                async () => {
                    await this.delete_history_image_confirmed(image);
                }
            );
        },

        /**
         * Actually delete history image after confirmation
         */
        async delete_history_image_confirmed(image) {
            try {
                const response = await this.call_ai_generation_api({
                    action: 'delete_image',
                    image_url: image.url
                });
                
                if (response.success) {
                    // Remove image from local array without refreshing from R2
                    const image_index = this.generation_history.findIndex(img => img.url === image.url);
                    if (image_index !== -1) {
                        this.generation_history.splice(image_index, 1);
                        // Update total count
                        this.history_total_images = Math.max(0, this.history_total_images - 1);
                    }
                    
                    // If this was the current preview image, reset to sample
                    if (this.ai_preview_image === image.url) {
                        this.ai_preview_image = './img/ai/sample.webp';
                        this.is_generated_image = false;
                    }
                    
                    this.show_floating_message(this.get_t('ai.image_deleted'), 'success');
                } else {
                    console.error('Failed to delete history image:', response.error);
                    this.show_floating_message(this.get_t('ai.error_deleting_image'), 'error');
                }
            } catch (error) {
                console.error('Error deleting history image:', error);
                this.show_floating_message(this.get_t('ai.error_deleting_image'), 'error');
            }
        },

        /**
         * Handle drag start for history images
         */
        handle_history_image_dragstart(event, image) {
            if (!image || !image.url) {
                event.preventDefault();
                return;
            }

            // Set drag effect and data - EXACTLY like the preview
            event.dataTransfer.effectAllowed = 'copy';
            event.dataTransfer.setData('text/ai-preview-url', image.url);
            
            // Create drag image preview
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = 100;
            canvas.height = 100;
            
            // Set semi-transparent background
            ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
            ctx.fillRect(0, 0, 100, 100);
            
            // Add drag text
            ctx.fillStyle = 'white';
            ctx.font = '12px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('AI Image', 50, 50);
            
            event.dataTransfer.setDragImage(canvas, 50, 50);
        },

        select_variant(variant_id) {
            this.current_variant = variant_id;
        },

        /**
         * Show history limit information modal
         */
        show_history_info_modal() {
            const user_tier = this.user_data?.tier || 'free';
            var tier_name = "free";

            if (user_tier === "premium")
            {
                tier_name = "Premium";
            }
            else if (user_tier === "creator_pro")
            {
                tier_name = "Creator Pro";
            }

            // Get max history from server data (already loaded in tier_limits)
            const max_history = this.tier_limits?.[user_tier]?.image_gen_history_count;
            
            const title = this.get_t('ai.history_info_title');
            const message = this.get_t('ai.history_info_message').replace('{max}', max_history).replace('{tier}', tier_name);
            
            // Use the same modal system as other confirmations
            this.show_confirmation_popup(title, message, () => {});
        },

        /**
         * Show hand fixer warning modal
         */
        show_hand_fixer_warning() {
            const title = this.get_t('ai.hand_fixer_warning_title');
            const message = this.get_t('ai.hand_fixer_warning_message');
            
            this.show_popup_with_content(title, message, { type: 'warning' });
        },

        /**
         * Handle model selection change
         */
        handle_model_change(new_model) {
            const old_model = this.ai_settings.model;
            
            // Save current scheduler preference for old model (if not Mikoniji)
            if (old_model && this.ai_settings.scheduler && !old_model.includes('mikoniji_semi')) {
                this.scheduler_preferences[old_model] = this.ai_settings.scheduler;
            }
            
            // Update model
            this.ai_settings.model = new_model;
            
            // Apply scheduler restrictions based on new model
            if (new_model.includes('mikoniji_semi')) {
                this.ai_settings.scheduler = 'karras';

                if (this.ai_settings.style == 3) {
                    // Switch to base if style was realistic (not supported by Mikoniji)
                    this.ai_settings.style = 1;
                }

            } else {
                this.ai_settings.scheduler = this.scheduler_preferences[new_model] || 'beta';
            }
            
            this.$forceUpdate();
        },
        
        /**
         * Handle click on disabled model - show warning message
         */
        handle_disabled_model_click(model) {
            if (!model || model.enabled) return;
            
            this.show_floating_message(this.get_t('ai.model_disabled_message'), 'warning');
        },
        
        /**
         * Handle click on disabled scheduler dropdown for Mikoniji
         */
        handle_scheduler_dropdown_click() {
            if (this.is_karras_only_model) {
                this.show_floating_message(this.get_t('ai.mikoniji_karras_only'), 'info');
            }
        }
    }
};

export default ai_generation_mixin;